// src/components/organisms/AddInteractionForm.js

import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import FormInputGroup from "../atoms/FormInputGroup";
import SelectMediaType from "../atoms/SelectMediaType";
import MediaUpload from "../molecules/MediaUpload";
import { useTheme } from "styled-components";
import PropTypes from "prop-types";

const AddInteractionForm = ({ initialTimestamp, onSubmit, onCancel }) => {
  const theme = useTheme();
  const [mediaType, setMediaType] = useState("image");
  const [mediaFile, setMediaFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [callToAction, setCallToAction] = useState("");
  const [callToActionURL, setCallToActionURL] = useState("");
  const [timestamp, setTimestamp] = useState(initialTimestamp || 0);

  useEffect(() => {
    setTimestamp(initialTimestamp || 0);
  }, [initialTimestamp]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !title ||
      !description ||
      !callToAction ||
      !callToActionURL ||
      !mediaFile
    ) {
      alert("Please fill in all fields and upload a media file.");
      return;
    }

    const interactionData = {
      title,
      description,
      callToAction,
      callToActionURL,
      mediaType,
      mediaFile,
      timestamp,
    };

    onSubmit(interactionData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box mb={2} mt={2}>
        {/* Label */}
        <label
          htmlFor="timestamp-input"
          style={{
            display: "block",
            marginBottom: "8px",
            fontFamily: "basic-sans",
            fontWeight: "bold",
            color: "white",
          }}
        >
          Timestamp (seconds)
        </label>

        {/* Input Field */}
        <input
          id="timestamp-input"
          type="number"
          value={timestamp}
          onChange={(e) => setTimestamp(Number(e.target.value))}
          readOnly
          required
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid #ccc", // Adjust for your theme
            backgroundColor: "#f9f9f9", // Adjust for your theme
            fontFamily: "basic-sans, sans-serif",
            fontSize: "16px",
            color: "#000", // Adjust for your theme
            outline: "none",
            boxSizing: "border-box",
          }}
        />

        {/* Helper Text */}
        <small
          style={{
            display: "block",
            marginTop: "4px",
            fontSize: "12px",
            color: "white",
            fontFamily: "basic-sans, sans-serif",
          }}
        >
          The interaction will appear at this timestamp.
        </small>
      </Box>

      <SelectMediaType
        mediaType={mediaType}
        onChange={(e) => setMediaType(e.target.value)}
      />
      <MediaUpload mediaType={mediaType} onFileSelect={setMediaFile} />
      <FormInputGroup
        id="title-input"
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <FormInputGroup
        id="description-input"
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        required
        multiline={true}
        rows={4}
      />
      <FormInputGroup
        id="button-text-input"
        label="Button Text"
        value={callToAction}
        onChange={(e) => setCallToAction(e.target.value)}
        required
      />
      <FormInputGroup
        id="button-link-input"
        label="Button Link"
        type="url"
        value={callToActionURL}
        onChange={(e) => setCallToActionURL(e.target.value)}
        required
      />
      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          onClick={onCancel}
          sx={{
            backgroundColor: theme.colors.darkGray,
            color: theme.colors.lightGray,
            borderRadius: "50px",
            padding: "10px 30px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.colors.lightGray,
              color: theme.colors.darkGray,
              boxShadow: "none",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          sx={{
            backgroundColor: theme.colors.green,
            color: theme.colors.white,
            borderRadius: "50px",
            padding: "10px 30px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.colors.blue,
              boxShadow: "none",
            },
          }}
        >
          Save Interaction
        </Button>
      </Box>
    </form>
  );
};

AddInteractionForm.propTypes = {
  initialTimestamp: PropTypes.number,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default AddInteractionForm;
